/**
 * Validate feature API layer.
 *
 * Base URL pattern: BASE() includes the service prefix (e.g. /test/v1/).
 * All paths start with `mission/…`.
 *
 * Response envelope: { status, message, recordsCount, data: T | T[] | null }
 * Actual payload at: res?.data?.data
 * Error message at:  err.response?.data?.message
 *
 * ── Key design changes (latest backend doc) ──────────────────────────────────
 * • GET /setups returns setup metadata. Sessions are loaded independently from
 *   GET /test/v1/mission/{missionId}/validate/sessions and are polled there.
 * • Dedicated session start/end PUT APIs are REMOVED. Session lifecycle is
 *   managed via the existing TrueTwin Deploy API (/truetwin/{id}/deploy).
 * • Ground-station providers response is now an array of objects
 *   { uniqueId, providerName, ... } instead of a key-value map.
 * • SessionStatus now includes SCHEDULED in addition to STARTED / ENDED.
 * • Setup now stores modelType + modelMode (from setup-config API).
 * • Session now stores testingEnvironment (from session-config API).
 */

import OperateService from '../../../service/OperateService';
import ValidateService from '../../../service/ValidateService';
import { getEnvVar } from '../../../envUtils';
import { showToastMessage } from '../../../utils/commonFunctions';
import type {
  ConfigOption,
  SatelliteOption,
  TestPlan,
  ValidateSession,
  ValidateSubsystemHealth,
  ValidateSetupType,
  ValidateSetup,
} from '../types/validateDetails.types';
import type { GroundStationProvider } from '../types/validateDetails.types';
import { normalizeSubsystemHealthStatus } from '../helpers/validateDetails.helpers';
import { StopInfo } from '../details/StopWarningBanner';

// ─── Error message extractor ──────────────────────────────────────────────────

/**
 * Reads err.response.data.message from the backend envelope.
 * Falls back to the provided fallback string if not present.
 */
export function extractApiError(err: unknown, fallback: string): string {
  if (err != null && typeof err === 'object' && 'response' in err) {
    const msg = (err as { response?: { data?: { message?: string } } })
      .response?.data?.message;
    if (msg && typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  return fallback;
}

// ─── Raw API shapes (backend contract) ────────────────────────────────────────

interface RawSession {
  sessionId: string;
  setupId: string;
  setupName?: string;
  setupIsDeleted?: boolean;
  satelliteId: string;
  missionId: string;
  tenantId: string;
  title: string;
  /** "SimulationRunning" | "SimulationCompleted" | "DeploymentInProgress" (current backend values) */
  sessionStatus: string;
  /** contact duration in minutes */
  contactDuration: number;
  /** Unix epoch seconds */
  startTime: number;
  /** Array of teamId strings */
  teams: string[];
  /** Testing environment identifier (e.g. "LAB", "TVAC", "Vibration"). */
  testingEnvironment?: string;
  /** Error notifications from backend (e.g. why a session failed) */
  notifications?: string[];
  /** Setup sanity health per subsystem. */
  subsystems?: unknown;
  /** Lock metadata */
  locked?: boolean;
  lastLockUpdateBy?: string;
  lockedBy?: string;
  lastLockUpdateAt?: string | number;
  lockedAt?: string | number;
}

interface RawSetup {
  setupId: string;
  name: string;
  description: string;
  satelliteId: string;
  missionId: string;
  groundStationProviderId: string;
  tenantId: string;
  version?: string;
  /** Sessions are embedded in GET /setups response */
  sessions: RawSession[];
  /** Model type (e.g. "EM", "FM", "SFM"). */
  modelType?: string;
  /** Model mode ("open" | "close"). */
  modelMode?: string;
  /** Setup type ("FLATSAT" | "SUBSYSTEM_UNIT_TESTING"). */
  setupType?: string;
  /** Subsystems connected to this setup. */
  connectedSubsystems?: unknown;
  /** Lock metadata */
  locked?: boolean;
  lastLockUpdateBy?: string;
  lockedBy?: string;
  lastLockUpdateAt?: string;
  lockedAt?: string;
}

/** Raw provider from GET /groundstation/GetProvidersBySatelliteId */
interface RawGroundStationProvider {
  uniqueId: string;
  providerName: string;
  IsAntarisGsiteProvider: boolean;
  status: string;
  missionId: string;
  tenantId: string;
}

// ─── Normalizers ─────────────────────────────────────────────────────────────


function normalizeSubsystemNameList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  return raw.reduce<string[]>((acc, item) => {
    const name = typeof item === 'string'
      ? item
      : item != null && typeof item === 'object'
        ? String(
          (item as Record<string, unknown>)['name']
          ?? (item as Record<string, unknown>)['label']
          ?? (item as Record<string, unknown>)['subsystem']
          ?? '',
        )
        : '';
    const trimmed = name.trim();
    const key = trimmed.toUpperCase();
    if (!trimmed || seen.has(key)) return acc;
    seen.add(key);
    acc.push(trimmed);
    return acc;
  }, []);
}

function normalizeFailedTestCaseList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  const seen = new Set<string>();
  const testCases = raw.reduce<string[]>((acc, item) => {
    // New API format: { name: string, testCaseResultId: string }
    // Old format: plain string
    const testCase = (
      item != null && typeof item === 'object' && !Array.isArray(item)
        ? String((item as Record<string, unknown>)['name'] ?? '').trim()
        : String(item ?? '').trim()
    );
    const key = testCase.toUpperCase();
    if (!testCase || seen.has(key)) return acc;
    seen.add(key);
    acc.push(testCase);
    return acc;
  }, []);

  return testCases.length ? testCases : undefined;
}

function normalizeSubsystemHealthList(raw: unknown): ValidateSubsystemHealth[] {
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  return raw.reduce<ValidateSubsystemHealth[]>((acc, item) => {
    if (typeof item === 'string') {
      const name = item.trim();
      const key = name.toUpperCase();
      if (!name || seen.has(key)) return acc;
      seen.add(key);
      acc.push({ name, healthStatus: 'UNKNOWN' });
      return acc;
    }

    if (item == null || typeof item !== 'object') return acc;

    const record = item as Record<string, unknown>;
    const name = String(record['name'] ?? record['label'] ?? record['subsystem'] ?? '').trim();
    const key = name.toUpperCase();
    if (!name || seen.has(key)) return acc;
    seen.add(key);

    const failedTestCases = normalizeFailedTestCaseList(
      record['failedTestCases'] ?? record['failedTestCaseIds'],
    );

    acc.push({
      name,
      healthStatus: normalizeSubsystemHealthStatus(
        record['healthStatus'] ?? record['status'] ?? record['result'],
      ),
      ...(failedTestCases?.length ? { failedTestCases } : {}),
    });
    return acc;
  }, []);
}

function normalizeSessionFromApi(raw: RawSession): ValidateSession {
  const statusMap: Record<string, ValidateSession['status']> = {
    // Current backend values
    SimulationRunning: 'RUNNING',
    SimulationCompleted: 'COMPLETED',
    DeploymentInProgress: 'DEPLOYING',
    Failed: 'FAILED',
    'Failed to start validation session': 'FAILED',
    // Legacy / alternate casing variants (kept for safety)
    STARTED: 'RUNNING',
    ENDED: 'COMPLETED',
    SCHEDULED: 'SCHEDULED',
    FAILED: 'FAILED',
    DEPLOYMENT_IN_PROGRESS: 'DEPLOYING',
    'deployment_in_progress': 'DEPLOYING',
  };
  const startTimeMs = raw.startTime > 100000000000 ? raw.startTime : raw.startTime * 1000;

  return {
    sessionId: raw.sessionId,
    sessionName: raw.title ?? (raw as { name?: string }).name ?? '',
    status: statusMap[raw.sessionStatus] ?? 'SCHEDULED',
    startedAt: raw.startTime ? new Date(startTimeMs).toISOString() : '',
    durationMinutes: raw.contactDuration ?? 0,
    teamName: raw.teams?.length ? raw.teams.join(', ') : '',
    // testingEnvironment will be populated once the backend returns it.
    // No UI change needed — the column shows '—' via the nullish coalescing in SessionRow.
    testingEnvironment: raw.testingEnvironment ?? '',
    setupId: raw.setupId ?? '',
    setupName: raw.setupName ?? '',
    setupIsDeleted: Boolean(raw.setupIsDeleted),
    subsystems: normalizeSubsystemHealthList(raw.subsystems),
    locked: Boolean((raw as any).locked),
    lastLockUpdateBy: (raw as any).lastLockUpdateBy || (raw as any).lockedBy,
    lockedBy: (raw as any).lastLockUpdateBy || (raw as any).lockedBy,
    lastLockUpdateAt: (raw as any).lastLockUpdateAt || (raw as any).lockedAt
      ? typeof ((raw as any).lastLockUpdateAt || (raw as any).lockedAt) === 'number'
        ? new Date((raw as any).lastLockUpdateAt || (raw as any).lockedAt).toISOString()
        : String((raw as any).lastLockUpdateAt || (raw as any).lockedAt)
      : undefined,
    lockedAt: (raw as any).lastLockUpdateAt || (raw as any).lockedAt
      ? typeof ((raw as any).lastLockUpdateAt || (raw as any).lockedAt) === 'number'
        ? new Date((raw as any).lastLockUpdateAt || (raw as any).lockedAt).toISOString()
        : String((raw as any).lastLockUpdateAt || (raw as any).lockedAt)
      : undefined,
  };
}

function normalizeSetupFromApi(raw: RawSetup): ValidateSetup {
  return {
    setupId: raw.setupId,
    setupName: raw.name,
    description: raw.description,
    satelliteId: raw.satelliteId,
    satelliteName: '',
    sessions: Array.isArray(raw.sessions)
      ? raw.sessions.map(normalizeSessionFromApi)
      : [],
    // New fields — passed through when the backend provides them.
    modelType: raw.modelType ?? '',
    modelMode: raw.modelMode ?? '',
    groundStationProviderId: raw.groundStationProviderId ?? '',
    groundStationProviderName: (raw as any).groundStationProviderName ?? (raw as any).providerName ?? '',
    setupType: raw.setupType ?? '',
    connectedSubsystems: normalizeSubsystemNameList(raw.connectedSubsystems),
    locked: Boolean((raw as any).locked),
    lastLockUpdateBy: (raw as any).lastLockUpdateBy || (raw as any).lockedBy,
    lockedBy: (raw as any).lastLockUpdateBy || (raw as any).lockedBy,
    lastLockUpdateAt: (raw as any).lastLockUpdateAt || (raw as any).lockedAt,
    lockedAt: (raw as any).lastLockUpdateAt || (raw as any).lockedAt,
  };
}

/** Fixed setup model types required by the current validate UX. */
const FIXED_MODEL_TYPE_OPTIONS: ConfigOption[] = [
  { label: 'EM', value: 'EM' },
  { label: 'QM', value: 'QM' },
  { label: 'PFM', value: 'PFM' },
  { label: 'FM', value: 'FM' },
];

/** Fixed testing-environment options required by the current validate UX. */
const FIXED_TESTING_ENV_OPTIONS: ConfigOption[] = [
  { label: 'LAB', value: 'LAB' },
  { label: 'TVAC', value: 'TVAC' },
  { label: 'Vibration', value: 'Vibration' },
  { label: 'Anechoic', value: 'Anechoic' },
];

function normalizeConfigOption(
  item: string | { name?: string; label?: string; value?: string; id?: string },
): ConfigOption | null {
  if (typeof item === 'string') {
    const trimmed = item.trim();
    return trimmed ? { label: trimmed, value: trimmed } : null;
  }

  const label = String(item.name ?? item.label ?? '').trim();
  const value = String(item.value ?? item.id ?? item.name ?? '').trim();

  if (!label && !value) return null;

  return {
    label: label || value,
    value: value || label,
  };
}

function normalizeFixedConfigOptions(
  raw: unknown,
  fixedOptions: ConfigOption[],
): ConfigOption[] {
  if (!Array.isArray(raw)) return fixedOptions;

  const normalized = raw
    .map((item) => normalizeConfigOption(item as string | { name?: string; label?: string; value?: string; id?: string }))
    .filter((item): item is ConfigOption => Boolean(item));

  if (normalized.length === 0) return fixedOptions;

  const byUpperValue = new Map(
    normalized.map((item) => [item.value.trim().toUpperCase(), item]),
  );

  const filtered = fixedOptions
    .map((fixedItem) => {
      const matched = byUpperValue.get(fixedItem.value.toUpperCase());
      return {
        label: fixedItem.label,
        value: matched?.value ?? fixedItem.value,
      };
    });

  return filtered.length > 0 ? filtered : fixedOptions;
}

function normalizeSubsystemConfigOptions(raw: unknown): ConfigOption[] {
  const payload = raw != null && typeof raw === 'object' && !Array.isArray(raw)
    ? ((raw as Record<string, unknown>)['subsystems'] ?? raw)
    : raw;

  if (!Array.isArray(payload)) return [];

  const seen = new Set<string>();
  return payload.reduce<ConfigOption[]>((acc, item) => {
    const option = normalizeConfigOption(item as string | { name?: string; label?: string; value?: string; id?: string });
    if (!option) return acc;

    const key = option.value.trim().toUpperCase();
    if (!key || seen.has(key)) return acc;

    seen.add(key);
    acc.push(option);
    return acc;
  }, []);
}

// ─── Eligible satellites ──────────────────────────────────────────────────────

interface EligibleSatelliteGroupRaw {
  name?: string;
  uniqueId?: string;
  satellites?: Array<{ uniqueId?: string; name?: string }>;
}

function isEligibleSatelliteList(raw: unknown): raw is EligibleSatelliteGroupRaw[] {
  return Array.isArray(raw);
}

export async function getEligibleSatellites(missionId: string): Promise<SatelliteOption[]> {
  const response = await ValidateService.getValidateCandidateSatelliteGroups(missionId);
  const raw = response?.data?.data;
  if (!isEligibleSatelliteList(raw)) return [];
  return raw.map((group: EligibleSatelliteGroupRaw) => {
    const firstSat = group?.satellites?.[0];
    return {
      label: group?.name ?? '',
      value: {
        groupId: group?.uniqueId ?? '',
        groupName: group?.name ?? '',
        satId: firstSat?.uniqueId ?? '',
        satName: firstSat?.name ?? '',
      },
    };
  });
}

// ─── Setups ───────────────────────────────────────────────────────────────────

export async function getSetupsBySatellite(
  missionId: string,
  satelliteGroupId: string,
  satelliteId: string,
): Promise<{ data: ValidateSetup[]; apiError: string | null }> {
  try {
    const res = await ValidateService.getSetupsBySatellite(missionId, satelliteGroupId, satelliteId);
    const payload = res?.data?.data;
    // null/undefined payload means backend returned empty — treat as no setups yet
    if (payload == null) return { data: [], apiError: null };
    const items: RawSetup[] = Array.isArray(payload) ? payload : [payload];
    return { data: items.map(normalizeSetupFromApi), apiError: null };
  } catch (err) {
    return { data: [], apiError: extractApiError(err, 'Failed to fetch setups') };
  }
}

export async function getValidationSessions(
  missionId: string,
  satelliteGroupId?: string,
  satelliteId?: string,
  setupId?: string,
): Promise<{ data: ValidateSession[]; apiError: string | null }> {
  try {
    const res = await ValidateService.getValidationSessions(missionId, {
      satelliteGroupId,
      satelliteId,
      setupId,
    });
    const payload = res?.data?.data;
    if (payload == null) return { data: [], apiError: null };
    const items: RawSession[] = Array.isArray(payload) ? payload : [payload];
    const sessions = items.map(normalizeSessionFromApi);


    return { data: sessions, apiError: null };
  } catch (err) {
    return { data: [], apiError: extractApiError(err, 'Failed to fetch sessions') };
  }
}

// ─── Setup Config (model types) ───────────────────────────────────────────────

/**
 * GET /test/v1/validate/additionalSetupConfigDetails
 */
export async function getSetupConfig(
  _missionId: string,
  _satelliteId: string,
): Promise<{ data: ConfigOption[]; apiError: string | null }> {
  try {
    const res = await ValidateService.getAdditionalSetupConfigDetails();
    const raw = res?.data?.data ?? res?.data;
    return {
      data: normalizeFixedConfigOptions(raw, FIXED_MODEL_TYPE_OPTIONS),
      apiError: null,
    };
  } catch (err) {
    return {
      data: FIXED_MODEL_TYPE_OPTIONS,
      apiError: extractApiError(err, 'Failed to fetch setup config'),
    };
  }
}

// ─── Available Subsystems ────────────────────────────────────────────────────

/**
 * GET /v1/mission/{missionId}/validate/subsystems
 */
export async function getAvailableSubsystems(
  missionId: string,
): Promise<{ data: ConfigOption[]; apiError: string | null }> {
  try {
    const res = await ValidateService.getAvailableSubsystems(missionId);
    const raw = res?.data?.data ?? res?.data;
    return {
      data: normalizeSubsystemConfigOptions(raw),
      apiError: null,
    };
  } catch (err) {
    return {
      data: [],
      apiError: extractApiError(err, 'Failed to fetch subsystems'),
    };
  }
}

// ─── Session Config (testing environments) ───────────────────────────────────

/**
 * GET /test/v1/validate/additionalSessionConfigDetails
 */
export async function getSessionConfig(
  _missionId: string,
  _satelliteId: string,
): Promise<{ data: ConfigOption[]; apiError: string | null }> {
  try {
    const res = await ValidateService.getAdditionalSessionConfigDetails();
    const raw = res?.data?.data ?? res?.data;
    return {
      data: normalizeFixedConfigOptions(raw, FIXED_TESTING_ENV_OPTIONS),
      apiError: null,
    };
  } catch (err) {
    return {
      data: FIXED_TESTING_ENV_OPTIONS,
      apiError: extractApiError(err, 'Failed to fetch session config'),
    };
  }
}

// ─── Setup Create / Delete ────────────────────────────────────────────────────

export type CreateSetupPayload = {
  name: string;
  groundStationProviderId?: string;
  description: string;
  /** Model type identifier (e.g. "EM", "FM"). Required once setup-config API ships. */
  modelType?: string;
  /** Model mode ("open" | "close"). Required once setup-config API ships. */
  modelMode?: string;
  /** Setup type selected by user. */
  setupType?: ValidateSetupType;
  /** Connected subsystems selected by the user at setup creation. */
  connectedSubsystems?: string[];
};

export async function createSetup(
  missionId: string,
  satelliteGroupId: string,
  satelliteId: string,
  payload: CreateSetupPayload,
): Promise<{ setup: ValidateSetup; message: string | null }> {
  const res = await ValidateService.createSetup(missionId, satelliteGroupId, satelliteId, {
    name: payload.name,
    description: payload.description,
    groundStationProviderId: payload.groundStationProviderId,
    // Pass through new fields when backend is ready to accept them
    ...(payload.modelType ? { modelType: payload.modelType } : {}),
    ...(payload.modelMode ? { modelMode: payload.modelMode } : {}),
    ...(payload.setupType ? { setupType: payload.setupType } : {}),
    ...(payload.connectedSubsystems?.length ? { connectedSubsystems: payload.connectedSubsystems } : {}),
  });
  const data: RawSetup | undefined = res?.data?.data;
  const msgRaw = (res as { data?: { message?: unknown } } | undefined)?.data?.message;
  const message = typeof msgRaw === 'string' && msgRaw.trim() ? msgRaw.trim() : null;
  if (data) return { setup: normalizeSetupFromApi(data), message };

  // Backend returned 200 with no body — construct locally (no server-generated id)
  return {
    setup: {
      setupId: '',
      setupName: payload.name,
      description: payload.description,
      satelliteId,
      satelliteName: '',
      sessions: [],
      modelType: payload.modelType,
      modelMode: payload.modelMode,
      setupType: payload.setupType,
      connectedSubsystems: payload.connectedSubsystems ?? [],
    },
    message,
  };
}

export async function deleteSetup(
  missionId: string,
  setupId: string,
  satelliteGroupId: string,
  satelliteId: string,
): Promise<string | null> {
  const res = await ValidateService.deleteSetup(missionId, setupId, satelliteGroupId, satelliteId);
  const msg = (res as { data?: { message?: unknown } } | undefined)?.data?.message;
  return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
}

// ─── Subsystem Diagram APIs ─────────────────────────────────────────────────

export type SetupSubsystemDiagram = {
  setupId: string;
  setupName: string;
  connectedSubsystems: Array<{ name: string }>;
};

export type SessionSubsystemDiagram = {
  sessionId: string;
  sessionName?: string;
  setupId?: string;
  setupName?: string;
  sanityTestPlanId?: string;
  sanityExecutionStatus?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | string;
  subsystems: ValidateSubsystemHealth[];
  updatedAt?: number | string | Date | null;
};

export type RunSanityTestResponse = {
  sessionId: string;
  sanityTestPlanId: string;
  sanityExecutionStatus?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | string;
  runId?: string;
  message?: string;
};

export async function getSetupSubsystemDiagram(
  missionId: string,
  satelliteGroupId: string,
  satelliteId: string,
  setupId: string,
): Promise<{ data: SetupSubsystemDiagram | null; apiError: string | null }> {
  try {
    const res = await ValidateService.getSetupSubsystemDiagram(
      missionId,
      satelliteGroupId,
      satelliteId,
      setupId,
    );
    const raw = res?.data?.data ?? res?.data;
    const record = raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? raw as Record<string, unknown>
      : {};
    const connectedSubsystems = normalizeSubsystemNameList(
      record['connectedSubsystems'] ?? record['subsystems'] ?? raw,
    );

    return {
      data: {
        setupId: String(record['setupId'] ?? setupId),
        setupName: String(record['setupName'] ?? record['name'] ?? 'Setup'),
        connectedSubsystems: connectedSubsystems.map((name) => ({ name })),
      },
      apiError: null,
    };
  } catch (err) {
    return {
      data: null,
      apiError: extractApiError(err, 'Failed to fetch setup subsystem diagram'),
    };
  }
}

export async function getSessionSubsystemDiagram(
  missionId: string,
  sessionId: string,
): Promise<{ data: SessionSubsystemDiagram | null; apiError: string | null }> {
  try {
    const res = await ValidateService.getSessionSubsystemDiagram(missionId, sessionId);
    const raw = res?.data?.data ?? res?.data;
    const record = raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? raw as Record<string, unknown>
      : {};
    const subsystems = normalizeSubsystemHealthList(record['subsystems'] ?? raw);

    return {
      data: {
        sessionId: String(record['sessionId'] ?? sessionId),
        sessionName: typeof record['sessionName'] === 'string'
          ? record['sessionName']
          : typeof record['name'] === 'string'
            ? record['name']
            : typeof record['title'] === 'string'
              ? record['title']
              : undefined,
        setupId: typeof record['setupId'] === 'string' ? record['setupId'] : undefined,
        setupName: typeof record['setupName'] === 'string' ? record['setupName'] : undefined,
        sanityTestPlanId: typeof record['sanityTestPlanId'] === 'string' ? record['sanityTestPlanId'] : undefined,
        sanityExecutionStatus: typeof record['sanityExecutionStatus'] === 'string' ? record['sanityExecutionStatus'] : undefined,
        subsystems,
        updatedAt: (() => {
          const val = record['updatedAt'];
          if (typeof val === 'number') {
            return val < 10000000000 ? val * 1000 : val;
          }
          if (typeof val === 'string' && val.trim() !== '') {
            return val;
          }
          return undefined;
        })(),
      },
      apiError: null,
    };
  } catch (err) {
    return {
      data: null,
      apiError: extractApiError(err, 'Failed to fetch session subsystem diagram'),
    };
  }
}

export async function runSessionSanityTest(
  missionId: string,
  sessionId: string,
  sanityTestPlanId: string,
): Promise<{ data: RunSanityTestResponse | null; apiError: string | null }> {
  try {
    const res = await ValidateService.runSessionSanityTest(missionId, sessionId, sanityTestPlanId);
    const body = res?.data as { message?: unknown; data?: unknown } | undefined;
    const raw = body?.data ?? body;
    const record = raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? raw as Record<string, unknown>
      : {};
    const message = typeof body?.message === 'string'
      ? body.message
      : typeof record['message'] === 'string'
        ? record['message']
        : undefined;

    return {
      data: {
        sessionId: String(record['sessionId'] ?? sessionId),
        sanityTestPlanId: String(record['sanityTestPlanId'] ?? sanityTestPlanId),
        sanityExecutionStatus: typeof record['sanityExecutionStatus'] === 'string'
          ? record['sanityExecutionStatus']
          : undefined,
        runId: typeof record['runId'] === 'string' ? record['runId'] : undefined,
        message,
      },
      apiError: null,
    };
  } catch (err) {
    return {
      data: null,
      apiError: extractApiError(err, 'Failed to start sanity test'),
    };
  }
}

// ─── Session Deploy ───────────────────────────────────────────────────────────

/** Request body for POST .../deploy/validate */
export type CreateSessionPayload = {
  name: string;
  testingEnvironment: string;
  setupId: string;
  teams: string[];
  setupSanityTestPlanId?: string;
};

/** Response from deploy API — quick async response with backend message. */
export type CreateSessionResponse = { message?: string };

export async function createSession(
  missionId: string,
  satelliteGroupId: string,
  satelliteId: string,
  payload: CreateSessionPayload,
): Promise<CreateSessionResponse> {
  const reqBody: CreateSessionPayload = {
    name: payload.name.trim(),
    testingEnvironment: payload.testingEnvironment,
    setupId: payload.setupId,
    teams: payload.teams,
    ...(payload.setupSanityTestPlanId ? { setupSanityTestPlanId: payload.setupSanityTestPlanId } : {}),
  };

  const res = await ValidateService.deployValidate(missionId, satelliteGroupId, satelliteId, reqBody);
  const body = res?.data as { message?: string; data?: { message?: string } } | undefined;
  const msg = body?.message ?? body?.data?.message;
  return { message: typeof msg === 'string' ? msg : undefined };
}

export async function endSession(
  missionId: string,
  scenarioId: string,
): Promise<void> {
  await (ValidateService as unknown as {
    undeployValidate: (missionId: string, scenarioId: string) => Promise<unknown>;
  }).undeployValidate(missionId, scenarioId);
}

export async function deleteSession(
  missionId: string,
  _setupId: string,
  sessionId: string,
): Promise<string | null> {
  // Reuse scenario delete endpoint for validate session delete.
  const res = await (ValidateService as unknown as {
    deleteScenario: (missionId: string, scenarioId: string) => Promise<unknown>;
  }).deleteScenario(missionId, sessionId);
  const msg = (res as { data?: { message?: unknown } } | undefined)?.data?.message;
  return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
}

export async function setTelecommandResult(
  commandId: string,
  result: 'pass' | 'fail' | 'timeout',
): Promise<void> {
  await (ValidateService as unknown as {
    patchTelecommandResult: (commandId: string, result: 'pass' | 'fail' | 'timeout') => Promise<unknown>;
  }).patchTelecommandResult(commandId, result);
}

export async function setTestCaseResult(
  testCaseId: string,
  result: 'pass' | 'fail' | 'timeout',
): Promise<void> {
  await (ValidateService as unknown as {
    patchTestCaseResult: (testCaseId: string, result: 'pass' | 'fail' | 'timeout') => Promise<unknown>;
  }).patchTestCaseResult(testCaseId, result);
}

// ─── Session Result ───────────────────────────────────────────────────────────

export interface SessionResultSummary {
  total: number;
  pass: number;
  fail: number;
  timeout?: number;
  partial: number;
  cancelled: number;
}

export type SessionResultStatus = 'PASS' | 'FAIL' | 'TIMEOUT' | 'PARTIAL' | 'PENDING' | 'CANCELLED';

export interface SessionResultItem {
  resultType: 'TestPlan' | 'TestCase';
  testPlanResultId?: string;
  testCaseResultId?: string;
  testPlanId?: string;
  testCaseId?: string;
  testPlanName?: string;
  testCaseName?: string;
  status: SessionResultStatus;
  rawStatus?: string;
  startTime?: number;
  endTime?: number;
  level?: string;
  tagType?: string;
  pass?: number;
  fail?: number;
  timeout?: number;
  duration?: string | number;
  subsystems?: string[];
  appliedFailureHandling?: string;
  stopInfo?: StopInfo;
  isCritical?: boolean;
}

export interface SessionResult {
  summary: SessionResultSummary;
  results: SessionResultItem[];
}

function normalizeSessionResultStatus(rawStatus: unknown): SessionResultStatus {
  const value = String(rawStatus ?? '').trim().toUpperCase();

  switch (value) {
    case 'PASS':
    case 'PASSED':
    case 'SUCCESS':
      return 'PASS';
    case 'FAIL':
    case 'FAILED':
      return 'FAIL';
    case 'TIMEOUT':
    case 'TIMED_OUT':
    case 'TIMEDOUT':
      return 'TIMEOUT';
    case 'PARTIAL':
      return 'PARTIAL';
    case 'CANCEL':
    case 'CANCELLED':
    case 'CANCELED':
    case 'ABORT':
    case 'ABORTED':
    case 'MANUAL ABORT':
    case 'MANUAL_ABORT':
      return 'CANCELLED';
    case 'PENDING':
      return 'PENDING';
    default:
      if (value.includes('ABORT') || value.includes('CANCEL')) {
        return 'CANCELLED';
      }
      return 'PENDING';
  }
}

function normalizeSessionResultType(rawType: unknown): 'TestPlan' | 'TestCase' | null {
  const value = String(rawType ?? '').trim().toUpperCase();
  if (value === 'TESTPLAN') return 'TestPlan';
  if (value === 'TESTCASE') return 'TestCase';
  return null;
}

function parseOptionalSessionResultNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;

    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }

  return undefined;
}

/**
 * GET /test/v1/mission/{missionId}/validate/sessions/{scenarioId}/result
 * Returns null on failure — caller falls back to computed stats.
 */
export async function getSessionResult(
  missionId: string,
  scenarioId: string,
): Promise<{ data: SessionResult | null; apiError: string | null }> {
  try {
    const res = await (ValidateService as unknown as {
      getSessionResult: (missionId: string, scenarioId: string) => Promise<unknown>;
    }).getSessionResult(missionId, scenarioId);

    const raw = (res as { data?: { data?: unknown; Summary?: unknown } } | undefined)?.data?.data
      ?? (res as { data?: unknown } | undefined)?.data;

    if (!raw || typeof raw !== 'object') return { data: null, apiError: null };

    const r = raw as Record<string, unknown>;
    const rawSummary =
      (r['summary'] as Record<string, unknown> | undefined)
      ?? (r['Summary'] as Record<string, unknown> | undefined);
    const rawResults = r['results'] ?? r['Results'];

    const summary: SessionResultSummary = {
      total: Number(rawSummary?.['total'] ?? rawSummary?.['Total'] ?? 0),
      pass: Number(rawSummary?.['pass'] ?? rawSummary?.['Pass'] ?? 0),
      fail: Number(rawSummary?.['fail'] ?? rawSummary?.['Fail'] ?? 0),
      timeout: Number(rawSummary?.['timeout'] ?? rawSummary?.['Timeout'] ?? 0),
      partial: Number(rawSummary?.['partial'] ?? rawSummary?.['Partial'] ?? 0),
      cancelled: Number(rawSummary?.['cancelled'] ?? rawSummary?.['Cancelled'] ?? rawSummary?.['Canceled'] ?? 0),
    };

    interface RawSessionResultItem {
      stopInfo?: StopInfo;
      StopInfo?: StopInfo;
      appliedFailureHandling?: string;
      AppliedFailureHandling?: string;
      appliedAbortPolicy?: string;
      AppliedAbortPolicy?: string;
      [key: string]: any;
    }

    const results: SessionResultItem[] = Array.isArray(rawResults)
      ? (rawResults as RawSessionResultItem[]).reduce<SessionResultItem[]>((acc, item) => {
        const resultType = normalizeSessionResultType(
          item['resultType'] ?? item['ResultType'] ?? item['Type'],
        );

        if (!resultType) return acc;

        const testPlanResultId = String(
          item['testPlanResultId'] ?? item['TestPlanResultId'] ?? '',
        ).trim() || undefined;

        const testCaseResultId = String(
          item['testCaseResultId'] ?? item['TestCaseResultId'] ?? '',
        ).trim() || undefined;

        const rawPass =
          item['pass'] ??
          item['Pass'] ??
          item['passCount'] ??
          item['PassCount'] ??
          item['testCasesPassed'] ??
          item['TestCasesPassed'] ??
          item['testCasesPass'] ??
          item['TestCasesPass'];
        const rawFail =
          item['fail'] ??
          item['Fail'] ??
          item['failCount'] ??
          item['FailCount'] ??
          item['testCasesFailed'] ??
          item['TestCasesFailed'] ??
          item['testCasesFail'] ??
          item['TestCasesFail'];
        const rawTimeout =
          item['timeout'] ??
          item['Timeout'] ??
          item['timeoutCount'] ??
          item['TimeoutCount'] ??
          item['testCasesTimeout'] ??
          item['TestCasesTimeout'] ??
          item['testCasesTimedOut'] ??
          item['TestCasesTimedOut'];
        const rawDuration = item['duration'] ?? item['Duration'] ?? item['durationMinutes'] ?? item['DurationMinutes'];
        const rawSubsystems = item['subsystems'] ?? item['Subsystems'] ?? item['components'] ?? item['Components'];
        const subsystems: string[] = Array.isArray(rawSubsystems)
          ? rawSubsystems.map((s: unknown) => String(s ?? '').trim()).filter(Boolean)
          : [];

        const rawStatusValue = item['status'] ?? item['Status'];
        const rawStatus = typeof rawStatusValue === 'string' && rawStatusValue.trim()
          ? rawStatusValue.trim()
          : undefined;

        const startTime =
          typeof item['startTime'] === 'number'
            ? item['startTime']
            : typeof item['StartTime'] === 'number'
              ? item['StartTime']
              : undefined;

        const endTime =
          typeof item['endTime'] === 'number'
            ? item['endTime']
            : typeof item['EndTime'] === 'number'
              ? item['EndTime']
              : undefined;

        acc.push({
          resultType,
          testPlanResultId,
          testCaseResultId,
          testPlanName: String(item['testPlanName'] ?? item['TestPlanName'] ?? '').trim() || undefined,
          testCaseName: String(item['testCaseName'] ?? item['TestCaseName'] ?? '').trim() || undefined,
          status: normalizeSessionResultStatus(rawStatusValue),
          rawStatus,
          startTime,
          endTime,
          level: item['level'] != null ? String(item['level']) : item['Level'] != null ? String(item['Level']) : undefined,
          tagType: item['type'] != null ? String(item['type']) : item['TypeLabel'] != null ? String(item['TypeLabel']) : undefined,
          pass: parseOptionalSessionResultNumber(rawPass),
          fail: parseOptionalSessionResultNumber(rawFail),
          timeout: parseOptionalSessionResultNumber(rawTimeout),
          duration:
            typeof rawDuration === 'number'
              ? rawDuration
              : typeof rawDuration === 'string' && rawDuration.trim()
                ? rawDuration.trim()
                : undefined,
          subsystems: subsystems.length ? subsystems : undefined,
          isCritical: Boolean(item['isCritical'] ?? item['IsCritical']),
          stopInfo: item.stopInfo ?? item.StopInfo ?? undefined,
          appliedFailureHandling: item.appliedFailureHandling ?? item.AppliedFailureHandling ?? item.appliedAbortPolicy ?? item.AppliedAbortPolicy ?? undefined,
        });

        return acc;
      }, [])
      : [];

    const totalTimeoutsInResults = results.filter(
      (r) => r.resultType === 'TestCase' && r.status === 'TIMEOUT',
    ).length;
    if (summary.timeout === 0 && totalTimeoutsInResults > 0) {
      summary.timeout = totalTimeoutsInResults;
    }

    const totalCancelledInResults = results.filter(
      (r) => r.resultType === 'TestCase' && r.status === 'CANCELLED',
    ).length;
    if (summary.cancelled === 0 && totalCancelledInResults > 0) {
      summary.cancelled = totalCancelledInResults;
    }

    return { data: { summary, results }, apiError: null };
  } catch (err) {
    return { data: null, apiError: extractApiError(err, 'Failed to fetch session result') };
  }
}

// ─── Report Download ──────────────────────────────────────────────────────────

/** Format options for report download */
export type ReportFormat = 'pdf-executive' | 'pdf-complete' | 'excel';

function getReportExtension(format: ReportFormat): 'pdf' | 'xlsx' {
  return format === 'excel' ? 'xlsx' : 'pdf';
}

function getSuggestedReportFileName(
  scope: 'test' | 'session',
  scenarioId: string,
  format: ReportFormat,
  testId?: string,
): string {
  const ext = getReportExtension(format);
  const modePart = format === 'pdf-executive' ? '-summary' : format === 'pdf-complete' ? '-complete' : '';
  if (scope === 'test' && testId) return `validate-test-${testId}${modePart}.${ext}`;
  return `validate-session-${scenarioId}${modePart}.${ext}`;
}

function getFileNameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // filename*=UTF-8''name.ext
  const utfMatch = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]).replace(/["']/g, '').trim();
    } catch {
      return utfMatch[1].replace(/["']/g, '').trim();
    }
  }
  // filename="name.ext"
  const plainMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (plainMatch?.[1]) return plainMatch[1].trim();
  return null;
}

async function triggerBrowserDownload(blob: Blob, fileName: string): Promise<void> {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

async function saveBlobWithPicker(blob: Blob, suggestedName: string, format: ReportFormat): Promise<boolean> {
  const savePicker = (window as unknown as {
    showSaveFilePicker?: (opts: {
      suggestedName: string;
      types: Array<{ description: string; accept: Record<string, string[]> }>;
    }) => Promise<{ createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }> }>;
  }).showSaveFilePicker;

  if (typeof savePicker !== 'function') return false;

  const ext = getReportExtension(format);
  const mime = ext === 'pdf'
    ? 'application/pdf'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  try {
    const handle = await savePicker({
      suggestedName,
      types: [
        {
          description: ext === 'pdf' ? 'PDF Document' : 'Excel Workbook',
          accept: { [mime]: [`.${ext}`] },
        },
      ],
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch (err) {
    const name = (err as { name?: string } | null)?.name;
    if (name === 'AbortError') {
      showToastMessage('info', 'Download cancelled');
      return true;
    }
    return false;
  }
}

async function downloadFileWithPrompt(downloadUrl: string, suggestedName: string, format: ReportFormat): Promise<void> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 45000);

  try {
    const response = await fetch(downloadUrl, {
      method: 'GET',
      credentials: 'include',
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);

    const blob = await response.blob();
    if (!blob || blob.size === 0) throw new Error('Downloaded file is empty');

    const headerName = getFileNameFromContentDisposition(response.headers.get('content-disposition'));
    const finalName = headerName || suggestedName;

    const savedViaPicker = await saveBlobWithPicker(blob, finalName, format);
    if (!savedViaPicker) {
      await triggerBrowserDownload(blob, finalName);
    }

    showToastMessage('success', 'Download completed');
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * Test-level report download — used by Reports tab download button.
 * URL: /sessions/{scenarioId}/tests/{testId}/report/pdf|excel/download-url
 */
export async function downloadTestReport(
  missionId: string,
  scenarioId: string,
  testId: string,
  format: ReportFormat,
): Promise<void> {
  showToastMessage('info', 'Preparing download…');

  try {
    const base = getEnvVar('REACT_APP_BACKEND_BASE_URL');
    let downloadUrl: string;

    if (format === 'excel') {
      const res = await (ValidateService as unknown as {
        getTestReportExcelDownloadToken: (m: string, s: string, t: string) => Promise<unknown>;
      }).getTestReportExcelDownloadToken(missionId, scenarioId, testId);

      const token = extractDownloadToken(res);
      if (!token) throw new Error('No download token received');

      downloadUrl = `${base}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/tests/${testId}/report/excel?dt=${token}`;
    } else {
      const mode: 'executive' | 'complete' = format === 'pdf-executive' ? 'executive' : 'complete';

      const res = await (ValidateService as unknown as {
        getTestReportPdfDownloadToken: (m: string, s: string, t: string, mode: string) => Promise<unknown>;
      }).getTestReportPdfDownloadToken(missionId, scenarioId, testId, mode);

      const token = extractDownloadToken(res);
      if (!token) throw new Error('No download token received');

      downloadUrl = `${base}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/tests/${testId}/report/pdf?dt=${token}&mode=${mode}`;
    }

    const suggestedName = getSuggestedReportFileName('test', scenarioId, format, testId);
    await downloadFileWithPrompt(downloadUrl, suggestedName, format);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Report download failed', err);
    showToastMessage('error', 'Failed to download report. Please try again.');
  }
}

/**
 * Session-level report download (no testPlanId — exports all test plans for a session).
 * Used by "Export All Reports" in the session three-dot menu.
 */
export async function downloadSessionReport(
  missionId: string,
  scenarioId: string,
  format: ReportFormat,
): Promise<void> {
  showToastMessage('info', 'Preparing download…');

  try {
    const base = getEnvVar('REACT_APP_BACKEND_BASE_URL');
    let downloadUrl: string;

    if (format === 'excel') {
      const res = await (ValidateService as unknown as {
        getSessionReportExcelDownloadToken: (m: string, s: string) => Promise<unknown>;
      }).getSessionReportExcelDownloadToken(missionId, scenarioId);

      const token = extractDownloadToken(res);
      if (!token) throw new Error('No download token received');

      downloadUrl = `${base}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/report/excel?dt=${token}`;
    } else {
      const mode: 'executive' | 'complete' = format === 'pdf-executive' ? 'executive' : 'complete';

      const res = await (ValidateService as unknown as {
        getSessionReportPdfDownloadToken: (m: string, s: string, mode: string) => Promise<unknown>;
      }).getSessionReportPdfDownloadToken(missionId, scenarioId, mode);

      const token = extractDownloadToken(res);
      if (!token) throw new Error('No download token received');

      downloadUrl = `${base}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/report/pdf?dt=${token}&mode=${mode}`;
    }

    const suggestedName = getSuggestedReportFileName('session', scenarioId, format);
    await downloadFileWithPrompt(downloadUrl, suggestedName, format);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Session report download failed', err);
    showToastMessage('error', 'Failed to download report. Please try again.');
  }
}

/** Extracts downloadToken from either a direct or enveloped API response */
function extractDownloadToken(res: unknown): string | null {
  const data = (res as { data?: Record<string, unknown> } | undefined)?.data;
  if (!data) return null;
  // Direct: { downloadToken: "..." }
  if (typeof data['downloadToken'] === 'string') return data['downloadToken'];
  // Enveloped: { data: { downloadToken: "..." } }
  const inner = data['data'] as Record<string, unknown> | undefined;
  if (inner && typeof inner['downloadToken'] === 'string') return inner['downloadToken'];
  return null;
}

// ─── Test Plans ───────────────────────────────────────────────────────────────

export async function getTestPlans(
  missionId: string,
  satelliteId: string,
): Promise<{ data: TestPlan[]; apiError: string | null }> {
  try {
    const res = await ValidateService.getAllTestPlans(missionId, satelliteId);
    const payload = res?.data?.data ?? res?.data;

    if (Array.isArray(payload)) {
      return { data: payload as TestPlan[], apiError: null };
    }

    return { data: [], apiError: null };
  } catch (err) {
    return {
      data: [],
      apiError: extractApiError(err, 'Failed to fetch test plans'),
    };
  }
}

// ─── Ground-station providers ─────────────────────────────────────────────────

export async function getGroundStationProviders(
  satelliteId: string,
): Promise<{ data: GroundStationProvider[]; apiError: string | null }> {
  try {
    const res = await OperateService.getProvider(satelliteId);
    const raw = res?.data;

    if (Array.isArray(raw)) {
      const providers = (raw as RawGroundStationProvider[])
        .filter((p) => p.status === 'ACTIVE' || !p.status)
        .map((p) => ({ label: p.providerName, value: p.uniqueId }));
      return { data: providers, apiError: null };
    }

    if (raw != null && typeof raw === 'object') {
      // Response shape: { [providerId]: { [band]: { ProviderName: string, ... } } }
      const providers = Object.entries(raw as Record<string, Record<string, { ProviderName?: string }>>)
        .map(([providerId, bands]) => {
          const firstBand = Object.values(bands ?? {})[0];
          const providerName = firstBand?.ProviderName ?? providerId;
          return { label: providerName, value: providerId };
        });
      return { data: providers, apiError: null };
    }

    return { data: [], apiError: null };
  } catch (err) {
    return {
      data: [],
      apiError: extractApiError(err, 'Failed to fetch ground station providers'),
    };
  }
}
